import { Meteor } from 'meteor/meteor';
import { Mongo } from 'meteor/mongo';
import { DeclareMethod } from '/imports/methods';
import { Doc, GetMethodResponse, ListMethodArg, ListMethodResponse, ModelClass, References } from '/imports/types';
import { validateString } from '/imports/validators';
import { strings } from '/imports/strings';
import { log } from '/imports/api/logs';
import { Org, modelClass as orgModelClass } from '/imports/api/orgs';
import { modelClass as userModelClass } from '/imports/api/users';
import { Account, modelClass as accountModelClass } from '/imports/api/accounts';
import { Message } from '/imports/api/messages';

export interface Conversation extends Doc {
  name?: string;
  orgIds?: string[];
  accountIds?: string[];
  userIds?: string[];
  orderId?: string;
  refundId?: string;
  // A project's chat. Access is NOT limited to the id lists below when this is set — anyone who can see
  // the project can see its conversation (see conversationGuard), so a newly linked stakeholder gets the
  // history with no backfill. The lists still work, and are how a super user adds someone from outside.
  projectManagementProjectId?: string;
  lastMessage?: Message;
  lastMessageAt?: Date;
}
export type Model = Conversation;

export const Conversations = new Mongo.Collection<Conversation>('conversations');

type Refs = References<Model, {
  orgIds: Org;
  accountIds: Account;
  userIds: Meteor.User;
}>

export const modelClass: ModelClass<Model, Refs> = {
  collection: Conversations,
  editableFields: ['name'],
  fieldLimits: {
    'name': 256,
  },
  listMethod: DeclareMethod<ListMethodArg<Conversation>, ListMethodResponse<Conversation, Refs>>('listConversations'),
  getMethod: DeclareMethod<string, GetMethodResponse<Conversation, Refs>>('getConversation'),
  createMethod: DeclareMethod<Partial<Conversation>, string>('createConversation'),
  updateMethod: DeclareMethod<Partial<Conversation>, string>('updateConversation'),
  deleteMethod: DeclareMethod<string>('deleteConversation'),
  pathPrefix: '/conversations',
  asString: (document) => document.name || document._id,
  modelName: strings.Conversation(),
  pluralName: strings.Conversations(),
  searchQuery: (search?: string) => {
    search = search?.trim();
    if (!search?.length) return {};
    return {
      $or: [
        { _id: search },
        { name: new RegExp(search, 'gi') },
      ]
    };
  },
  validate: async (conversation: Partial<Conversation>) => {
    const errors: { [key: string]: string[] } = {};
    validateString(errors, conversation, 'name');
    return errors;
  },
  references: {
    orgIds: { modelClass: orgModelClass, fields: { name: 1 } },
    userIds: { modelClass: userModelClass, fields: { username: 1 } },
    accountIds: { modelClass: accountModelClass, fields: { firstName: 1, lastName: 1 } },
  },
};

/** Fetch (or lazily create) the conversation associated with an org. */
export const getConversationForOrg = DeclareMethod<string, GetMethodResponse<Conversation, Refs>>('getConversationForOrg');
/** The project's conversation, created on first use. */
export const projectManagementConversation = DeclareMethod<string, GetMethodResponse<Conversation, Refs>>('projectManagementConversation');
/** Super-user only: add an account or a user to a project's conversation. */
export const addConversationParticipant = DeclareMethod<{ conversationId: string, accountId?: string, userId?: string }, void>('addConversationParticipant');
export const removeConversationParticipant = DeclareMethod<{ conversationId: string, accountId?: string, userId?: string }, void>('removeConversationParticipant');

/** One row in the participant picker: an account anywhere on the platform, or a super user. */
export interface ConversationCandidate {
  _id: string;
  name: string;
  email?: string;
  org?: string;     // the account's org — a super user sees every org, and one person can hold an account in several
  kind: 'account' | 'superUser';
}

/** Who the caller may talk to: a super user sees every account (any org) plus every super user; anyone
 *  else sees the active accounts of their own org. */
export const listConversationCandidates = DeclareMethod<undefined, ConversationCandidate[]>('listConversationCandidates');
/** Starts (or reopens) an ad-hoc conversation between the caller and the chosen accounts / super users. */
export const startConversation = DeclareMethod<{ accountIds: string[], userIds?: string[], name?: string }, string>('startConversation');

Meteor.isServer && Meteor.startup(async () => {
  await Conversations.createIndexAsync({ orgIds: 1 }, { sparse: true });
  await Conversations.createIndexAsync({ accountIds: 1 }, { sparse: true });
  await Conversations.createIndexAsync({ userIds: 1 }, { sparse: true });
  // Unique: get-or-create races on first open, and a project must never end up with two chats. The per-task
  // and per-document threads of early Oct 2026 (an `anchor` on the row) are gone: their rows, messages and
  // receipts are removed here once, and the one-per-project index returns in place of the anchored one.
  const threadIds = await Conversations.find({ anchor: { $exists: true } } as any, { fields: { _id: 1 } }).mapAsync((c) => c._id);
  if (threadIds.length) {
    const { Messages, MessageReceipts } = await import('/imports/api/messages');
    const messages = await Messages.removeAsync({ conversationId: { $in: threadIds } });
    const receipts = await MessageReceipts.removeAsync({ conversationId: { $in: threadIds } });
    await Conversations.removeAsync({ _id: { $in: threadIds } });
    log('info', 'conversations: removed task/document threads', { threads: threadIds.length, messages, receipts }, { tags: ['startupCleanup'] });
  }
  try { await Conversations.rawCollection().dropIndex('projectManagementProjectId_1_anchor.kind_1_anchor.id_1'); }
  catch (_) { /* already gone */ }
  await Conversations.createIndexAsync({ projectManagementProjectId: 1 }, { sparse: true, unique: true });
  await Conversations.createIndexAsync({ orderId: 1 }, { sparse: true, unique: true });
  await Conversations.createIndexAsync({ refundId: 1 }, { sparse: true, unique: true });
});
